import React, {Component, createRef, RefObject, TouchEvent as ReactTouchEvent, MouseEvent as ReactMouseEvent} from 'react';
import './index.scss';
import UpArrow from 'assets/img/icons/up-arrow-key.svg';
import LeftArrow from 'assets/img/icons/left-arrow-key.svg';
import DownArrow from 'assets/img/icons/down-arrow-key.svg';
import RightArrow from 'assets/img/icons/right-arrow-key.svg';
import config from 'config';
import {getOrigin} from 'util/url';
import {getAuthToken} from 'services/auth';
import {Button} from '@material-ui/core';

interface DesignWalkThroughProps {
  design?: string;
  className?: string;
}

export default class DesignWalkThrough extends Component<DesignWalkThroughProps> {
  private static CONTROLS_DEADZONE: number = 0.1;

  private frameRef: RefObject<HTMLIFrameElement> = createRef();
  private movementButtonRef:RefObject<HTMLButtonElement> = createRef();
  private previewerInitialized: boolean = false;
  private onPreviewerInitialized:(()=>void)|null = null;

  private get frameOrigin():string {
    const frame = this.frameRef.current;
    if(!frame) {
      return '';
    }
    return getOrigin(frame.src);
  }

  componentDidMount(): void {
    window.addEventListener('message', this.onWindowMessage);
    if(this.props.design) {
      this.loadDesign(this.props.design);
    }
  }

  componentWillUnmount(): void {
    window.removeEventListener('message', this.onWindowMessage);
  }

  componentDidUpdate(prevProps: Readonly<DesignWalkThroughProps>, prevState: Readonly<{}>, snapshot?: any) {
    if(this.props.design !== prevProps.design && this.props.design) {
      this.loadDesign(this.props.design);
    }
  }

  onWindowMessage = (ev:MessageEvent) => {
      if(ev.origin !== this.frameOrigin) {
        return;
      }
      if(ev.data.type === 'previewerInitialized') {
        console.log('Previewer initialized');
        this.previewerInitialized = true;
        const frame = this.frameRef.current;
        const frameWindow = frame ? frame.contentWindow : null;
        if(frameWindow) {
          frameWindow.postMessage({
            'type': 'configure',
            'apiURL': config.api.url,
            'authToken': getAuthToken()
          }, this.frameOrigin);
        }
        if(this.onPreviewerInitialized) {
          this.onPreviewerInitialized();
          this.onPreviewerInitialized = null;
        }
      } else {
      }
  };

  loadDesign(code: string) {
    if(!this.previewerInitialized) {
      this.onPreviewerInitialized = () => this.loadDesign(code);
      return;
    }
    this.sendFrameMessage({'type': 'loadDesign', 'code': code});
  }

  loadDesignScreen(designCode: string, code: string) {
    if(!this.previewerInitialized) {
      this.onPreviewerInitialized = () => this.loadDesignScreen(designCode, code);
      return;
    }
    this.sendFrameMessage({'type': 'loadDesignScreenGroup', 'code': code});
  }

  loadSpace(space: string, screens:{[index:string]:string}|undefined = undefined) {
    if(!this.previewerInitialized) {
      this.onPreviewerInitialized = () => this.loadSpace(space, screens);
      return;
    }
    this.sendFrameMessage({'type': 'loadSpace', 'space': space, 'screens': screens});
  }

  unloadScreenGroup(code: string) {
    this.sendFrameMessage({'type': 'unloadScreenGroup', 'code': code});
  }

  unloadSpace() {
    this.sendFrameMessage({'type': 'unloadSpace'});
  }

  onChangeLayoutClick = () => {
    this.sendFrameMessage({'type': 'cycleLayout'});
  };

  private sendFrameMessage(message: any) {
    const frame = this.frameRef.current;
    const frameWindow = frame ? frame.contentWindow : null;
    if(frameWindow) {
      frameWindow.postMessage(message, this.frameOrigin);
    }
  }

  onMovementTouchStart = (ev: ReactTouchEvent) => {
    document.addEventListener('touchend', this.onMovementTouchEnd);
    document.addEventListener('touchmove', this.onMovementTouchMove);
    this.updateMovementVelocity(ev.touches[0].clientX, ev.touches[0].clientY);
    // stop iframe from capturing mouse events
    const frame = this.frameRef.current;
    if(frame) {
      frame.style.pointerEvents = 'none';
    }
  };

  onMovementTouchEnd = () => {
    this.sendFrameMessage({'type': 'setMovementVelocity', 'movement': 0, 'rotation': 0});
    document.removeEventListener('touchend', this.onMovementTouchEnd);
    document.removeEventListener('touchmove', this.onMovementTouchMove);
    // restore iframe mouse events
    const frame = this.frameRef.current;
    if(frame) {
      frame.style.pointerEvents = '';
    }
  };

  onMovementTouchMove = (ev: TouchEvent) => {
    ev.preventDefault();
    this.updateMovementVelocity(ev.touches[0].clientX, ev.touches[0].clientY);
  };

  onMovementMouseDown = (ev: ReactMouseEvent) => {
    document.addEventListener('mouseup', this.onMovementMouseUp);
    document.addEventListener('mousemove', this.onMovementMouseMove);
    this.updateMovementVelocity(ev.clientX, ev.clientY);
    // stop iframe from capturing mouse events
    const frame = this.frameRef.current;
    if(frame) {
      frame.style.pointerEvents = 'none';
    }
  };

  onMovementMouseUp = () => {
    this.sendFrameMessage({'type': 'setMovementVelocity', 'movement': 0, 'rotation': 0});
    document.removeEventListener('mouseup', this.onMovementMouseUp);
    document.removeEventListener('mousemove', this.onMovementMouseMove);
    // restore iframe mouse events
    const frame = this.frameRef.current;
    if(frame) {
      frame.style.pointerEvents = '';
    }
  };

  onMovementMouseMove = (ev: MouseEvent) => {
    ev.preventDefault();
    this.updateMovementVelocity(ev.clientX, ev.clientY);
  };

  private updateMovementVelocity(mouseX: number, mouseY: number) {
    const movementElement = this.movementButtonRef.current;
    if(!movementElement) {
      return;
    }
    const movementRect = movementElement.getBoundingClientRect();
    let rotationVelocity = Math.min(1, Math.max(-1, 2  * (mouseX - (movementRect.x + movementRect.width * 0.5)) / movementRect.width));
    if(Math.abs(rotationVelocity) <= DesignWalkThrough.CONTROLS_DEADZONE) {
      rotationVelocity = 0;
    }
    let movementVelocity = Math.min(1, Math.max(-1, -2  * (mouseY - (movementRect.y + movementRect.height * 0.5)) / movementRect.height));
    if(Math.abs(movementVelocity) <= DesignWalkThrough.CONTROLS_DEADZONE) {
      movementVelocity = 0;
    }
    this.sendFrameMessage({'type': 'setMovementVelocity', 'movement': movementVelocity, 'rotation': rotationVelocity});
  }

  focus() {
    const frame = this.frameRef.current;
    const frameWindow = frame ? frame.contentWindow : null;
    if(frameWindow) {
      frameWindow.focus();
    }
  }

  render() {
    const className = this.props.className ? `design-walkthrough ${this.props.className}` : 'design-walkthrough';
    return <div className={className}>
      <div className="top-bar">
        <div className="movement-instructions">
          <p>Use the arrow keys to walk around</p>
          <div className="diagram">
            <span><img src={UpArrow} alt="Up arrow" /></span>
            <span><img src={LeftArrow} alt="Left arrow" /></span>
            <span><img src={DownArrow} alt="Down arrow" /></span>
            <span><img src={RightArrow} alt="Right arrow" /></span>
          </div>
        </div>
      </div>
      <div className="frame">
        <Button className="cycle-layout" onClick={this.onChangeLayoutClick}>Change Layout</Button>
        <iframe src={config.spaces.previewURL} title="Space tour" ref={this.frameRef} />
      </div>
      <div className="controls">
        <button className="movement" ref={this.movementButtonRef}
                onTouchStart={this.onMovementTouchStart} onMouseDown={this.onMovementMouseDown} />
      </div>
    </div>;
  }
}